import { DEFAULT_COUNTRY, InternationalAddress } from '../models/international-address.model';

// US phone in common formats — used only to drop phone-only lines from the street.
const PHONE_REGEX = /(\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4})/;

// "City, ST 12345" / "City ST 12345-6789" — the line that ends a US address block.
const CITY_STATE_ZIP_REGEX = /^(.*?)[,\s]+([A-Za-z]{2})\s+(\d{5})(?:-\d{4})?\s*$/;

// Whole-line country names we recognize; anything else defaults to U.S.
const COUNTRY_BY_LINE: Record<string, string> = {
    'usa': DEFAULT_COUNTRY,
    'u.s.': DEFAULT_COUNTRY,
    'u.s.a.': DEFAULT_COUNTRY,
    'united states': DEFAULT_COUNTRY,
    'united states of america': DEFAULT_COUNTRY,
    'canada': 'Canada',
    'mexico': 'Mexico',
};

// A line that starts with a number or "PO Box" is a street, not a recipient name.
const STREET_START_REGEX = /^\s*(\d|p\.?\s*o\.?\s*box)/i;

/**
 * Best-effort parse of a free-text address block into a structured InternationalAddress.
 * Addresses are stored as free text on purpose (pasted from a PO, with inline contact
 * info, etc.), so this is a first guess the caller confirms/corrects — it is not
 * authoritative. Country defaults to U.S. unless a country line is recognized.
 *
 * Because people often include unused info (a recipient name, a phone number, an
 * attention contact), the parser does some guessing: the City/State/ZIP line is found
 * by scanning bottom-up; the street lines are the lines above it, where the first line
 * is treated as a recipient/company name and dropped unless it looks like a street;
 * phone-only and recognized-country lines are ignored.
 */
export function parseAddressString(raw: string): InternationalAddress {
    const address: InternationalAddress = {
        line1: '',
        line2: '',
        city: '',
        region: '',
        postalCode: '',
        country: DEFAULT_COUNTRY,
    };

    const lines = (raw || '').split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) return address;

    // Country: if any whole line names a country, use it (and skip that line later).
    let countryLineIdx = -1;
    for (let i = 0; i < lines.length; i++) {
        const match = COUNTRY_BY_LINE[lines[i].toLowerCase()];
        if (match) {
            address.country = match;
            countryLineIdx = i;
            break;
        }
    }

    // City / region / postal — scan bottom-up for "City ST 12345".
    let cityLineIdx = -1;
    for (let i = lines.length - 1; i >= 0; i--) {
        if (i === countryLineIdx) continue;
        const m = lines[i].match(CITY_STATE_ZIP_REGEX);
        if (m) {
            cityLineIdx = i;
            address.city = m[1].trim().replace(/,+$/, '').trim();
            address.region = m[2].toUpperCase();
            address.postalCode = m[3];
            break;
        }
    }

    // Street lines: lines above the city line, skipping the first line if it looks like
    // a recipient/company name (not a street), plus phone-only and country lines.
    const streetEnd = cityLineIdx === -1 ? lines.length : cityLineIdx;
    const startIdx = STREET_START_REGEX.test(lines[0]) ? 0 : 1;

    const streetLines: string[] = [];
    for (let i = startIdx; i < streetEnd; i++) {
        if (i === countryLineIdx) continue;
        const isPhoneOnly = !!lines[i].match(PHONE_REGEX) && lines[i].replace(PHONE_REGEX, '').trim().length === 0;
        if (isPhoneOnly) continue;
        streetLines.push(lines[i]);
    }

    address.line1 = streetLines[0] ?? '';
    // Anything past the first street line collapses into line2 so nothing is lost.
    address.line2 = streetLines.slice(1).join(', ');
    return address;
}
